import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import * as d3 from 'd3';
import { 
  Network, 
  Activity, 
  Brain,
  Database,
  Eye,
  Settings,
  Play,
  MessageSquare,
  CheckCircle2,
  AlertCircle,
  BarChart3,
} from 'lucide-react';
import { Card } from '../ui/Card'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { cn } from '../../utils/cn'
import { NOT_MEASURED, NO_DATA_SOURCE, NotMeasuredNotice } from './NotMeasured'

interface AgentCluster {
  id: string;
  name: string;
  type: 'model' | 'tool' | 'coordinator' | 'monitor';
  region: string;
  agentCount: number;
  maxCapacity: number;
  healthyAgents: number;
  busyAgents: number;
  avgLatency: number;
  throughput: number;
  position: { x: number; y: number };
  connections: string[];
}

interface AgentCommunication {
  from: string;
  to: string;
  messageType: 'request' | 'response' | 'event' | 'heartbeat';
  timestamp: Date;
  latency: number;
  size: number;
  status: 'success' | 'pending' | 'failed';
}

// No data source feeds this page yet (GH #3969). Clusters and messages stay
// empty and every metric reads NOT_MEASURED until the Conductor's run records
// are wired (GH #3958); the page starts no simulation and invents no cluster,
// count, latency or message.
const NO_CLUSTERS: AgentCluster[] = [];
const NO_COMMUNICATIONS: AgentCommunication[] = [];

const METRIC_TILES = [
  'Total Agents',
  'Tasks/Second',
  'Coordination Latency',
  'Consensus Rounds',
] as const;

const CLUSTER_COLORS = {
  model: '#8b5cf6',
  tool: '#10b981',
  coordinator: '#f59e0b',
  monitor: '#3b82f6',
} as const;

const CLUSTER_ICONS = {
  model: Brain,
  tool: Database,
  coordinator: Network,
  monitor: Eye,
} as const;

export const DistributedAgentOrchestration: React.FC = () => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedCluster, setSelectedCluster] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'topology' | 'metrics' | 'communications'>('topology');

  const clusters = NO_CLUSTERS;
  const communications = NO_COMMUNICATIONS;
  
  // D3 visualization
  useEffect(() => {
    if (!svgRef.current || viewMode !== 'topology') {
return;
}
    
    const svg = d3.select(svgRef.current);
    
    svg.selectAll('*').remove();
    
    const g = svg.append('g');
    
    // Create separate layers for proper z-ordering
    const connectionsLayer = g.append('g').attr('class', 'connections-layer');
    const animationsLayer = g.append('g').attr('class', 'animations-layer');
    const clustersLayer = g.append('g').attr('class', 'clusters-layer');
    const labelsLayer = g.append('g').attr('class', 'labels-layer');
    
    // Draw connections
    clusters.forEach(cluster => {
      cluster.connections.forEach(targetId => {
        const target = clusters.find(c => c.id === targetId);
        if (!target) {
return;
}
        
        connectionsLayer.append('line')
          .attr('x1', cluster.position.x)
          .attr('y1', cluster.position.y)
          .attr('x2', target.position.x)
          .attr('y2', target.position.y)
          .attr('stroke', '#374151')
          .attr('stroke-width', 2)
          .attr('opacity', 0.6);
      });
    });
    
    // Draw clusters
    clusters.forEach(cluster => {
      const radius = Math.max(30, Math.sqrt(cluster.agentCount) / 2);
      const utilization = cluster.agentCount / cluster.maxCapacity;
      
      // Cluster background
      clustersLayer.append('circle')
        .attr('cx', cluster.position.x)
        .attr('cy', cluster.position.y)
        .attr('r', radius + 5)
        .attr('fill', '#1f2937')
        .attr('stroke', CLUSTER_COLORS[cluster.type])
        .attr('stroke-width', 2)
        .attr('opacity', 0.8);
      
      // Utilization fill
      clustersLayer.append('circle')
        .attr('cx', cluster.position.x)
        .attr('cy', cluster.position.y)
        .attr('r', radius * utilization)
        .attr('fill', CLUSTER_COLORS[cluster.type])
        .attr('opacity', 0.6);
      
      // Selection ring
      if (selectedCluster === cluster.id) {
        clustersLayer.append('circle')
          .attr('cx', cluster.position.x)
          .attr('cy', cluster.position.y)
          .attr('r', radius + 10)
          .attr('fill', 'none')
          .attr('stroke', '#ffffff')
          .attr('stroke-width', 3)
          .attr('stroke-dasharray', '5,5');
      }
      
      // Agent count background (subtle shadow for readability)
      labelsLayer.append('text')
        .attr('x', cluster.position.x)
        .attr('y', cluster.position.y)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#000000')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .attr('opacity', 0.5)
        .style('pointer-events', 'none')
        .style('filter', 'blur(2px)')
        .text(cluster.agentCount.toLocaleString());
      
      // Agent count - in labels layer to stay on top
      labelsLayer.append('text')
        .attr('x', cluster.position.x)
        .attr('y', cluster.position.y)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#ffffff')
        .attr('font-size', '14px')
        .attr('font-weight', 'bold')
        .style('pointer-events', 'none')
        .text(cluster.agentCount.toLocaleString());
      
      // Cluster name - in labels layer to stay on top
      labelsLayer.append('text')
        .attr('x', cluster.position.x)
        .attr('y', cluster.position.y + radius + 20)
        .attr('text-anchor', 'middle')
        .attr('fill', '#e5e7eb')
        .attr('font-size', '12px')
        .style('pointer-events', 'none')
        .text(cluster.name);
      
      // Click handler
      clustersLayer.append('circle')
        .attr('cx', cluster.position.x)
        .attr('cy', cluster.position.y)
        .attr('r', radius + 5)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('click', () => setSelectedCluster(
          selectedCluster === cluster.id ? null : cluster.id,
        ));
    });
    
    // Animate communication flows with curved paths
    if (communications.length > 0) {
      communications.slice(0, 5).forEach((comm, index) => {
        const fromCluster = clusters.find(c => c.id === comm.from);
        const toCluster = clusters.find(c => c.id === comm.to);
        if (!fromCluster || !toCluster) {
return;
}
        
        // Calculate control point for curved path
        const dx = toCluster.position.x - fromCluster.position.x;
        const dy = toCluster.position.y - fromCluster.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const midX = (fromCluster.position.x + toCluster.position.x) / 2;
        const midY = (fromCluster.position.y + toCluster.position.y) / 2;
        
        // Offset perpendicular to the line for curve
        const offsetDistance = distance * 0.15;
        const perpX = -dy / distance * offsetDistance;
        const perpY = dx / distance * offsetDistance;
        
        // Create curved path
        const path = d3.path();
        path.moveTo(fromCluster.position.x, fromCluster.position.y);
        path.quadraticCurveTo(
          midX + perpX,
          midY + perpY,
          toCluster.position.x,
          toCluster.position.y
        );
        
        // Draw path (invisible, just for animation)
        const pathElement = animationsLayer.append('path')
          .attr('d', path.toString())
          .attr('fill', 'none')
          .attr('stroke', 'none');
        
        const pathLength = pathElement.node()?.getTotalLength() || 0;
        
        // Animated dot - smaller and more subtle
        const message = animationsLayer.append('circle')
          .attr('cx', fromCluster.position.x)
          .attr('cy', fromCluster.position.y)
          .attr('r', 2.5)
          .attr('fill', '#60a5fa')
          .attr('opacity', 0.6);
        
        // Animate along the path
        message.transition()
          .duration(1500)
          .delay(index * 300)
          .attrTween('cx', function() {
            return function(t: number) {
              const point = pathElement.node()?.getPointAtLength(t * pathLength);
              return String(point ? point.x : fromCluster.position.x);
            };
          })
          .attrTween('cy', function() {
            return function(t: number) {
              const point = pathElement.node()?.getPointAtLength(t * pathLength);
              return String(point ? point.y : fromCluster.position.y);
            };
          })
          .on('end', function () {
            d3.select(this).remove();
            pathElement.remove();
          });
      });
    }
    
  }, [clusters, selectedCluster, viewMode, communications]);
  
  const selectedClusterData = selectedCluster ? 
    clusters.find(c => c.id === selectedCluster) : null;
  
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Distributed Agent Orchestration</h3>
            <p className="text-sm text-gray-400">
              Agent coordination across regions. No data source is connected, so nothing on this page is measured.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary">{NO_DATA_SOURCE}</Badge>
          </div>
        </div>

        {/* Coordination Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {METRIC_TILES.map((label) => (
            <div key={label} className="bg-gray-800 rounded-lg p-3">
              <div className="text-sm font-medium text-gray-500">{NOT_MEASURED}</div>
              <div className="text-sm text-gray-400">{label}</div>
            </div>
          ))}
        </div>

        {/* View Mode Toggle */}
        <div className="flex space-x-1 mb-4 p-1 bg-gray-800 rounded-lg w-fit">
          {[
            { id: 'topology', label: 'Topology', icon: Network },
            { id: 'metrics', label: 'Metrics', icon: BarChart3 },
            { id: 'communications', label: 'Communications', icon: MessageSquare },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setViewMode(tab.id as typeof viewMode)}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all',
                viewMode === tab.id
                  ? 'bg-primary-600 text-white'
                  : 'text-gray-400 hover:text-white hover:bg-gray-700/50',
              )}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content based on view mode */}
        {viewMode === 'topology' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Agent Mesh Topology */}
            <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-300 mb-3">Agent Mesh Topology</h4>
              {clusters.length > 0 ? (
                <svg ref={svgRef} width="800" height="500" className="w-full h-auto" />
              ) : (
                <NotMeasuredNotice message="No agent clusters are reported: no data source is connected." />
              )}
            </div>

            {/* Cluster Details */}
            <div className="space-y-4">
              {selectedClusterData ? (
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    {React.createElement(CLUSTER_ICONS[selectedClusterData.type], {
                      className: 'w-5 h-5',
                      style: { color: CLUSTER_COLORS[selectedClusterData.type] },
                    })}
                    <h4 className="text-sm font-medium text-white">{selectedClusterData.name}</h4>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Capacity</span>
                        <span className="text-white">{selectedClusterData.agentCount}/{selectedClusterData.maxCapacity}</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2 mt-1">
                        <div 
                          className="h-2 rounded-full"
                          style={{ 
                            width: `${(selectedClusterData.agentCount / selectedClusterData.maxCapacity) * 100}%`,
                            backgroundColor: CLUSTER_COLORS[selectedClusterData.type],
                          }}
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <span className="text-gray-400">Healthy:</span>
                        <span className="text-green-400 ml-1">{selectedClusterData.healthyAgents}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Busy:</span>
                        <span className="text-yellow-400 ml-1">{selectedClusterData.busyAgents}</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Latency:</span>
                        <span className="text-primary-400 ml-1">{selectedClusterData.avgLatency}ms</span>
                      </div>
                      <div>
                        <span className="text-gray-400">Throughput:</span>
                        <span className="text-purple-400 ml-1">{selectedClusterData.throughput}/s</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-800 rounded-lg p-4 text-center text-gray-400">
                  <Network className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Click a cluster to view details</p>
                </div>
              )}

              {/* Quick Actions */}
              <div className="bg-gray-800 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">Cluster Operations</h4>
                <div className="space-y-2">
                  <Button variant="primary" size="sm" className="w-full text-xs">
                    <Play className="w-3 h-3 mr-1" />
                    Scale Up Agents
                  </Button>
                  <Button variant="secondary" size="sm" className="w-full text-xs">
                    <Settings className="w-3 h-3 mr-1" />
                    Configure Cluster
                  </Button>
                  <Button variant="secondary" size="sm" className="w-full text-xs">
                    <Activity className="w-3 h-3 mr-1" />
                    View Health
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {viewMode === 'metrics' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-300 mb-3">Performance Metrics</h4>
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Agent Utilization</span>
                  <span className="text-gray-500">{NOT_MEASURED}</span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-400">Messages Sent</div>
                    <div className="text-gray-500">{NOT_MEASURED}</div>
                  </div>
                  <div>
                    <div className="text-gray-400">State Conflicts</div>
                    <div className="text-gray-500">{NOT_MEASURED}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-300 mb-3">Cluster Health</h4>
              {clusters.length === 0 && (
                <NotMeasuredNotice message="No cluster health is reported: no data source is connected." />
              )}
              <div className="space-y-2">
                {clusters.map(cluster => (
                  <div key={cluster.id} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: CLUSTER_COLORS[cluster.type] }}
                      />
                      <span className="text-sm text-gray-300">{cluster.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{cluster.agentCount}</span>
                      {cluster.healthyAgents === cluster.agentCount ? (
                        <CheckCircle2 className="w-4 h-4 text-green-400" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-yellow-400" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {viewMode === 'communications' && (
          <div className="bg-gray-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-300 mb-3">Agent Communications</h4>
            {communications.length === 0 && (
              <NotMeasuredNotice message="No agent messages are recorded: no data source is connected." />
            )}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {communications.map((comm, index) => {
                const fromCluster = clusters.find(c => c.id === comm.from);
                const toCluster = clusters.find(c => c.id === comm.to);
                
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center justify-between p-2 bg-gray-700 rounded text-xs"
                  >
                    <div className="flex items-center gap-2">
                      <MessageSquare className="w-3 h-3 text-primary-400" />
                      <span className="text-gray-300">
                        {fromCluster?.name} → {toCluster?.name}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="text-xs">{comm.messageType}</Badge>
                      <span className="text-gray-400">{comm.latency.toFixed(1)}ms</span>
                      <span className="text-gray-500">{comm.timestamp.toLocaleTimeString()}</span>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default DistributedAgentOrchestration;